(() => {
  const config = window.__themeGlobalExitFadeConfig || {};
  const mobileBp = Number(config.mobileBp || 749);
  const tabletBp = Number(config.tabletBp || 991);
  const markerSelector = '[data-global-exit-fade-marker="true"]';

  let entries = [];
  let updateRaf = null;
  let refreshRaf = null;

  function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }

  function toNumber(value, fallback) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  function getFadeAmount() {
    return clamp(toNumber(config.fadeAmount, 100), 0, 100);
  }

  function getFadeMinOpacity() {
    return 1 - (getFadeAmount() / 100);
  }

  function getFadeBandRatio() {
    return clamp(toNumber(config.fadeBandSize, 32), 10, 60) / 100;
  }

  function getResponsiveOffsets() {
    if (window.innerWidth <= mobileBp) {
      return {
        top: toNumber(config.topMobile, 0),
        bottom: toNumber(config.bottomMobile, 0)
      };
    }
    if (window.innerWidth <= tabletBp) {
      return {
        top: toNumber(config.topTablet, 0),
        bottom: toNumber(config.bottomTablet, 0)
      };
    }
    return {
      top: toNumber(config.topDesktop, 0),
      bottom: toNumber(config.bottomDesktop, 0)
    };
  }

  function clearTarget(target) {
    if (!target) return;
    target.style.opacity = '';
  }

  function buildEntries() {
    const nextEntries = [];
    const previousTargets = new Set(entries.map((entry) => entry.target).filter(Boolean));

    document.querySelectorAll(markerSelector).forEach((marker) => {
      if (!marker || !marker.isConnected) return;
      const sectionRoot = marker.closest('[id^="shopify-section-"]');
      if (!sectionRoot) return;

      const targetSelector = marker.dataset.targetSelector || '';
      if (!targetSelector) return;

      let target = null;
      try {
        target = sectionRoot.querySelector(targetSelector);
      } catch (_) {
        target = null;
      }
      if (!target || !target.isConnected) return;

      previousTargets.delete(target);
      nextEntries.push({
        marker,
        target,
        disableMobile: marker.dataset.disableMobile === 'true'
      });
    });

    previousTargets.forEach((target) => clearTarget(target));
    entries = nextEntries;
  }

  function getTopEdge(extraOffset) {
    if (window.__themeMotionUtils && typeof window.__themeMotionUtils.getActiveViewportTopEdge === 'function') {
      return window.__themeMotionUtils.getActiveViewportTopEdge(extraOffset);
    }
    const vh = window.innerHeight || document.documentElement.clientHeight || 0;
    if (vh <= 0) return 0;
    return Math.min(vh - 1, Math.max(0, toNumber(extraOffset, 0)));
  }

  function applyEntryFade(entry, topEdge, bottomEdge, fadeMin, fadeBandRatio) {
    const target = entry.target;
    if (!target || !target.isConnected) return;

    if (entry.disableMobile && window.innerWidth <= mobileBp) {
      clearTarget(target);
      return;
    }

    const rect = target.getBoundingClientRect();
    const edgeBand = Math.max(80, Math.round((bottomEdge - topEdge) * fadeBandRatio));
    let opacity = 1;

    if (rect.bottom <= topEdge || rect.top >= bottomEdge) {
      opacity = fadeMin;
    } else {
      let progress = 0;
      if (rect.bottom > topEdge && rect.bottom < (topEdge + edgeBand)) {
        progress = Math.max(progress, 1 - ((rect.bottom - topEdge) / edgeBand));
      }
      if (rect.top < bottomEdge && rect.top > (bottomEdge - edgeBand)) {
        progress = Math.max(progress, 1 - ((bottomEdge - rect.top) / edgeBand));
      }
      progress = Math.pow(progress, 0.65);
      opacity = Math.max(fadeMin, 1 - ((1 - fadeMin) * progress));
    }

    target.style.opacity = opacity.toFixed(4);
  }

  function updateAll() {
    updateRaf = null;

    entries = entries.filter((entry) => (
      entry &&
      entry.marker &&
      entry.marker.isConnected &&
      entry.target &&
      entry.target.isConnected
    ));
    if (!entries.length) return;

    const fadeAmount = getFadeAmount();
    if (fadeAmount <= 0) {
      entries.forEach((entry) => clearTarget(entry.target));
      return;
    }

    const vh = window.innerHeight || document.documentElement.clientHeight || 0;
    if (vh <= 0) return;

    const offsets = getResponsiveOffsets();
    const topEdge = getTopEdge(offsets.top);
    const bottomEdge = Math.max(topEdge + 1, Math.min(vh, vh - offsets.bottom));
    const fadeMin = getFadeMinOpacity();
    const fadeBandRatio = getFadeBandRatio();

    entries.forEach((entry) => {
      applyEntryFade(entry, topEdge, bottomEdge, fadeMin, fadeBandRatio);
    });
  }

  function scheduleUpdate() {
    if (updateRaf) return;
    updateRaf = window.requestAnimationFrame(updateAll);
  }

  function refreshEntries() {
    refreshRaf = null;
    buildEntries();
    scheduleUpdate();
  }

  function scheduleRefresh() {
    if (refreshRaf) return;
    refreshRaf = window.requestAnimationFrame(refreshEntries);
  }

  function invalidateHeaderCandidates() {
    if (window.__themeMotionUtils && typeof window.__themeMotionUtils.invalidateHeaderCandidates === 'function') {
      window.__themeMotionUtils.invalidateHeaderCandidates();
    }
  }

  function handleViewportChange() {
    invalidateHeaderCandidates();
    scheduleRefresh();
  }

  function bindGlobalEvents() {
    window.addEventListener('scroll', scheduleUpdate, { passive: true });
    window.addEventListener('resize', handleViewportChange, { passive: true });
    window.addEventListener('load', handleViewportChange, { once: true });
    window.addEventListener('pageshow', handleViewportChange);
    window.addEventListener('orientationchange', handleViewportChange, { passive: true });

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleViewportChange, { passive: true });
    }

    document.addEventListener('shopify:section:load', handleViewportChange);
    document.addEventListener('shopify:section:unload', handleViewportChange);
  }

  function init() {
    if (window.__themeGlobalExitFadeManager) {
      window.__themeGlobalExitFadeManager.refresh();
      return;
    }

    bindGlobalEvents();
    window.__themeGlobalExitFadeManager = {
      refresh() {
        handleViewportChange();
      }
    };
    scheduleRefresh();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
